/**
 * Market Configuration Module
 * 
 * Centralized configuration for all markets (India, UAE, Saudi Arabia, UK, Singapore, USA, Europe)
 * Provides market detection, currency conversion, and catalog prefixing
 */
window.MarketConfig = {
  // Primary market (India) - Razorpay
  default: {
    market: 'INR',
    currency: 'INR',
    gateway: 'razorpay',
    catalogPrefix: '',
    locale: 'en',
    path: '/',
    countryCode: 'IN',
    phoneCountryCode: '+91',
    currencySymbol: '₹',
    conversionRate: 1, // No conversion needed
    stripeEnabled: false,
    razorpayMagicCheckoutEnabled: true // Magic Checkout enabled for India
  },
  
  // International markets - Stripe
  markets: {
    'en-ae': {
      market: 'UAE',
      currency: 'AED',
      gateway: 'stripe',
      catalogPrefix: 'uae_',
      locale: 'en-ae',
      path: '/en-ae/',
      countryCode: 'AE',
      phoneCountryCode: '+971',
      currencySymbol: 'AED',
      conversionRate: 0.04, // INR to AED (update with actual rate)
      stripeEnabled: true,
      razorpayMagicCheckoutEnabled: false // Magic Checkout disabled for UAE
    },
    'ar-ae': {
      market: 'UAE',
      currency: 'AED',
      gateway: 'stripe',
      catalogPrefix: 'uae_',
      locale: 'ar-ae',
      path: '/ar-ae/',
      countryCode: 'AE',
      phoneCountryCode: '+971',
      currencySymbol: 'AED',
      conversionRate: 0.04,
      stripeEnabled: true,
      razorpayMagicCheckoutEnabled: false
    },
    'en-sa': {
      market: 'Saudi Arabia',
      currency: 'SAR',
      gateway: 'stripe',
      catalogPrefix: 'uae_', // Use UAE catalog for all international markets
      locale: 'en-sa',
      path: '/en-sa/',
      countryCode: 'SA',
      phoneCountryCode: '+966',
      currencySymbol: 'SAR',
      conversionRate: 0.04, // INR to SAR (update with actual rate)
      stripeEnabled: true,
      razorpayMagicCheckoutEnabled: false
    },
    'ar-sa': {
      market: 'Saudi Arabia',
      currency: 'SAR',
      gateway: 'stripe',
      catalogPrefix: 'uae_',
      locale: 'ar-sa',
      path: '/ar-sa/',
      countryCode: 'SA',
      phoneCountryCode: '+966',
      currencySymbol: 'SAR',
      conversionRate: 0.04,
      stripeEnabled: true,
      razorpayMagicCheckoutEnabled: false
    },
    'en-gb': {
      market: 'UK',
      currency: 'GBP',
      gateway: 'stripe',
      catalogPrefix: 'uae_', // Use UAE catalog for all international markets
      locale: 'en-gb',
      path: '/en-gb/',
      countryCode: 'GB',
      phoneCountryCode: '+44',
      currencySymbol: '£',
      conversionRate: 0.0083, // INR to GBP (update with actual rate)
      stripeEnabled: true,
      razorpayMagicCheckoutEnabled: false
    },
    'en-sg': {
      market: 'Singapore',
      currency: 'SGD',
      gateway: 'stripe',
      catalogPrefix: 'uae_', // Use UAE catalog for all international markets
      locale: 'en-sg',
      path: '/en-sg/',
      countryCode: 'SG',
      phoneCountryCode: '+65',
      currencySymbol: 'S$',
      conversionRate: 0.016, // INR to SGD (update with actual rate)
      stripeEnabled: true,
      razorpayMagicCheckoutEnabled: false
    },
    'en-us': {
      market: 'USA',
      currency: 'USD',
      gateway: 'stripe',
      catalogPrefix: 'uae_', // Use UAE catalog for all international markets
      locale: 'en-us',
      path: '/en-us/',
      countryCode: 'US',
      phoneCountryCode: '+1',
      currencySymbol: '$',
      conversionRate: 0.0111, // INR to USD (update with actual rate)
      stripeEnabled: true,
      razorpayMagicCheckoutEnabled: false
    },
    'en-eu': {
      market: 'Europe',
      currency: 'EUR',
      gateway: 'stripe',
      catalogPrefix: 'uae_', // Use UAE catalog for all international markets
      locale: 'en-eu',
      path: '/en-eu/',
      countryCode: 'EU', // Will need country-specific handling
      phoneCountryCode: '+33', // Default to France
      currencySymbol: '€',
      conversionRate: 0.0095, // INR to EUR (update with actual rate)
      stripeEnabled: true,
      razorpayMagicCheckoutEnabled: false
    },
    'en-ca': {
      market: 'Canada',
      currency: 'CAD',
      gateway: 'stripe',
      catalogPrefix: 'uae_', // Use UAE catalog for all international markets
      locale: 'en-ca',
      path: '/en-ca/',
      countryCode: 'CA',
      phoneCountryCode: '+1',
      currencySymbol: 'CA$',
      conversionRate: 0.012, // INR to CAD (placeholder, update with actual rate)
      stripeEnabled: true,
      razorpayMagicCheckoutEnabled: false
    },
    'en-au': {
      market: 'Australia',
      currency: 'AUD',
      gateway: 'stripe',
      catalogPrefix: 'uae_', // Use UAE catalog for all international markets
      locale: 'en-au',
      path: '/en-au/',
      countryCode: 'AU',
      phoneCountryCode: '+61',
      currencySymbol: 'A$',
      conversionRate: 0.018, // INR to AUD (placeholder, update with actual rate)
      stripeEnabled: true,
      razorpayMagicCheckoutEnabled: false
    }
  },
  
  /**
   * Detect current market from URL, hostname, or locale
   * @returns {Object} { key: string, config: Object }
   */
  detectMarket: function() {
    const pathname = window.location.pathname;
    const hostname = window.location.hostname;
    
    // Check path-based detection (handle both with and without trailing slash)
    for (const [key, config] of Object.entries(this.markets)) {
      // Check if pathname starts with the market path (with or without trailing slash)
      const pathPattern = new RegExp('^' + config.path.replace(/\/$/, '') + '(?:/|$)');
      if (pathPattern.test(pathname) || pathname.includes(config.path) || hostname.includes(key)) {
        return { key, config };
      }
    }
    
    // Check window.shopifyLocale if available
    if (window.shopifyLocale && window.shopifyLocale.code) {
      const locale = window.shopifyLocale.code;
      if (this.markets[locale]) {
        return { key: locale, config: this.markets[locale] };
      }
    }
    
    // Default to India
    return { key: 'default', config: this.default };
  },
  
  /**
   * Get market config for a specific locale
   * @param {string} locale - Locale code (e.g., 'en-ae', 'en-sa')
   * @returns {Object} Market configuration
   */
  getMarketConfig: function(locale) {
    return this.markets[locale] || this.default;
  },
  
  /**
   * Check if current market is international (not India)
   * @returns {boolean}
   */
  isInternationalMarket: function() {
    const marketInfo = this.detectMarket();
    return marketInfo.key !== 'default';
  },
  
  /**
   * Convert price from INR (cents) to market currency
   * @param {number} priceInCents - Price in INR cents (Shopify format)
   * @param {string} marketKey - Optional market key, if not provided uses detected market
   * @returns {number} Price in market currency
   */
  convertPrice: function(priceInCents, marketKey) {
    const marketInfo = marketKey ? { config: this.markets[marketKey] || this.default } : this.detectMarket();
    const config = marketInfo.config;
    
    if (!config || config.conversionRate === 1) {
      return priceInCents / 100; // Return in base currency (INR)
    }
    
    const inrAmount = priceInCents / 100;
    return inrAmount * config.conversionRate;
  },
  
  /**
   * Prefix content IDs for Meta catalog matching
   * @param {Array|string} ids - Content IDs to prefix
   * @param {string} marketKey - Optional market key, if not provided uses detected market
   * @returns {Array} Prefixed content IDs
   */
  prefixContentIds: function(ids, marketKey) {
    const marketInfo = marketKey ? { config: this.markets[marketKey] || this.default } : this.detectMarket();
    const config = marketInfo.config;
    
    if (!config || !config.catalogPrefix) {
      // Return as array if input was array, otherwise return as-is
      return Array.isArray(ids) ? ids : [ids];
    }
    
    const prefix = config.catalogPrefix;
    if (!Array.isArray(ids)) {
      ids = [ids];
    }
    
    return ids.map(id => {
      if (!id) return null;
      const idStr = id.toString();
      // If already has prefix, return as-is
      if (idStr.startsWith(prefix)) {
        return idStr;
      }
      // Add prefix
      return prefix + idStr;
    }).filter(id => id !== null);
  },
  
  /**
   * Get current market info (cached after first call)
   * @returns {Object} { key: string, config: Object }
   */
  getCurrentMarket: function() {
    if (!this._currentMarket) {
      this._currentMarket = this.detectMarket();
    }
    return this._currentMarket;
  },
  
  /**
   * Reset cached market (useful for testing or when locale changes)
   */
  resetMarketCache: function() {
    this._currentMarket = null;
  }
};

// Initialize current market on load
if (typeof window !== 'undefined') {
  // Store detected market for quick access
  window.currentMarket = window.MarketConfig ? window.MarketConfig.getCurrentMarket() : null;
  // Removed console.log for production performance
}

